import { useMemo } from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { prefixes as prefixesBase, useTypedTranslation as useTypedTranslationBase } from 'i18next-typesafe';
import type { TranslationKey } from '@/types/i18n.generated';

import en from './en.json';
import he from './he.json';

i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        fallbackLng: 'he',
        debug: false,

        resources: {
            en: { translation: en },
            he: { translation: he },
        },

        detection: {
            order: ['localStorage', 'navigator', 'htmlTag'],
            caches: ['localStorage']
        }
    });

// UI languages - languages the site interface can be translated to
export const UI_LANGUAGES = ['he', 'en'] as const;
export type UILanguageCode = typeof UI_LANGUAGES[number];

export const UI_LANGUAGE_OPTIONS = [
    { code: 'he' as const, label: 'עברית' },
    { code: 'en' as const, label: 'English' }
];

// Content languages - languages for product descriptions/content
export const CONTENT_LANGUAGES = [
    'he',  // Hebrew
    'en',  // English
    'es',  // Spanish
    'ru',  // Russian
    'pt',  // Portuguese
    'de',  // German
    'it',  // Italian
    'nl',  // Dutch
    'sv',  // Swedish
    'pl',  // Polish
    'cs',  // Czech
    'yi',  // Yiddish (important for Judaica!)
    'ar',  // Arabic (for Middle Eastern customers)
] as const;
export type LanguageCode = typeof CONTENT_LANGUAGES[number];

export type LanguageContent = {
    [K in LanguageCode]?: {
        name: string;
        description: string;
        location?: string;
    }
}

/**
 * Maps language codes to locale codes for date/number formatting
 * @param lang - Language code (e.g., 'en', 'he')
 * @returns Locale code (e.g., 'en-US', 'he-IL')
 */
export const getLocaleCode = (lang: string): string => {
    const localeMap: Record<string, string> = {
        'he': 'he-IL',
        'en': 'en-US',
        'es': 'es-ES',
        'ru': 'ru-RU',
        'pt': 'pt-BR',
        'de': 'de-DE',
        'it': 'it-IT',
        'nl': 'nl-NL',
        'sv': 'sv-SE',
        'pl': 'pl-PL',
        'cs': 'cs-CZ',
        'yi': 'yi',
        'ar': 'ar-SA',
    };
    return localeMap[lang] || 'en-US';
};

// Type-safe translation hook with stable function references
export function useTypedTranslation<Prefixes extends readonly string[]>(
    prefixes: Prefixes
) {
    const translators = useTypedTranslationBase<TranslationKey, Prefixes>(prefixes);

    // Memoize based on current language for optimal stability
    // - Functions are stable within the same language (prevents infinite loops)
    // - Functions update when language changes (maintains reactivity)
    // - Safe to include in dependency arrays (satisfies exhaustive-deps)
    const currentLanguage = i18n.language;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => translators, [currentLanguage]);
}

export { prefixesBase as prefixes };
export { i18n };
